// frontend/src/pages/Dashboard.jsx

import React, { useState, useEffect, useMemo } from "react";
import axiosAuth from "../axiosConfig"; // baseURL = .../api/auth
import axios from "axios";             // for creating tradesAPI
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

// ─────────────────────────────────────────────────────────────────────────────
// Palette & chart colors (kept close to Withdraw page aesthetic)
const COLORS = ["#1D4ED8", "#3B82F6", "#60A5FA", "#93C5FD", "#BFDBFE"];
const EMPTY_COLOR = "#E0E7FF";

// Number formatting helpers
const formatINR = (n) =>
  `₹ ${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatNum = (n, max = 8) =>
  Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: max,
  });

// Lightweight skeleton block for loading states
const Skeleton = ({ className = "" }) => (
  <div
    className={`animate-pulse bg-blue-100/60 dark:bg-slate-700/60 rounded-xl ${className}`}
  />
);

const Dashboard = () => {
  const [user, setUser]             = useState(null);
  const [balances, setBalances]     = useState(null);
  const [orders, setOrders]         = useState([]);
  const [marketData, setMarketData] = useState([]);
  const [mdUpdatedAt, setMdUpdatedAt] = useState(null);
  const [error, setError]           = useState("");
  const [loading, setLoading]       = useState(true);
  const navigate = useNavigate();

  // tradesAPI for orders (safe fallback if REACT_APP_API_URL undefined)
  const apiRoot = (process.env.REACT_APP_API_URL || "").replace(/\/api\/auth\/?$/, "");
  const tradesAPI = axios.create({
    baseURL: apiRoot ? `${apiRoot}/api/trades` : `/api/trades`,
    withCredentials: true,
  });

  // ──────────────────────────────────────────────────────────────────────────
  // 1) Fetch user → balances → orders (one shot)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        // whoami
        const whoamiRes = await axiosAuth.get("/whoami");
        const usr = whoamiRes?.data?.user;
        setUser(usr);

        // balances
        const balRes = await axiosAuth.get(`/balance/${encodeURIComponent(usr.email)}`);
        const rawBal = balRes?.data?.balances ?? balRes?.data ?? {};
        setBalances(rawBal);

        // orders
        const ordRes = await tradesAPI.get("/orders");
        setOrders(ordRes?.data?.orders || []);
      } catch (err) {
        console.error("[Dashboard] Error loading data:", err);
        setError("Failed to load dashboard data. Please log in.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // ──────────────────────────────────────────────────────────────────────────
  // 2) Market data from CoinGecko (load once + refresh every 60s)
  useEffect(() => {
    let timer;
    const loadMarket = () => {
      fetch(
        "https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,tether,ripple&vs_currencies=inr&include_24hr_change=true"
      )
        .then((r) => r.json())
        .then((data) => {
          const md = [];
          if (data?.bitcoin) {
            md.push({ pair: "BTC/INR", price: data.bitcoin.inr, change: data.bitcoin.inr_24h_change });
          }
          if (data?.ethereum) {
            md.push({ pair: "ETH/INR", price: data.ethereum.inr, change: data.ethereum.inr_24h_change });
          }
          if (data?.tether) {
            md.push({ pair: "USDT/INR", price: data.tether.inr, change: data.tether.inr_24h_change });
          }
          if (data?.ripple) {
            md.push({ pair: "XRP/INR", price: data.ripple.inr, change: data.ripple.inr_24h_change });
          }
          setMarketData(md);
          setMdUpdatedAt(new Date());
        })
        .catch((err) => console.error("Error fetching market data:", err));
    };

    loadMarket();
    timer = setInterval(loadMarket, 60_000);
    return () => clearInterval(timer);
  }, []);

  // ──────────────────────────────────────────────────────────────────────────
  // 3) priceLookup { BTC: price, ETH: price, USDT: price, XRP: price }
  const priceLookup = useMemo(() => {
    const map = {};
    marketData.forEach(({ pair, price }) => {
      const coin = pair.split("/")[0];
      map[coin] = price;
    });
    return map;
  }, [marketData]);

  // 4) Compute totalValue in INR: sum( cryptoBalance * itsINRprice ) + raw INR
  const totalValueINR = useMemo(() => {
    if (!balances) return 0;
    return Object.entries(balances).reduce((sum, [coin, amt]) => {
      const n = Number(amt) || 0;
      return sum + (coin === "INR" ? n : n * (priceLookup[coin] || 0));
    }, 0);
  }, [balances, priceLookup]);

  // 5) Pie data weighted by INR value
  const pieDataINR = useMemo(() => {
    if (!balances) return [];
    const arr = Object.entries(balances).map(([currency, amount]) => {
      const n = Number(amount) || 0;
      const inrValue = currency === "INR" ? n : n * (priceLookup[currency] || 0);
      return { name: currency, value: inrValue };
    });
    return arr.filter((d) => d.value > 0);
  }, [balances, priceLookup]);

  // Mobile balance order
  const MOBILE_BALANCE_ORDER = ["USDT", "BTC", "ETH", "INR"];
  const sortedBalanceEntries = useMemo(() => {
    if (!balances) return [];
    const entries = Object.entries(balances);
    const setPref = new Set(MOBILE_BALANCE_ORDER);
    const pref = [];
    const rest = [];
    entries.forEach(([k, v]) => (setPref.has(k) ? pref.push([k, v]) : rest.push([k, v])));
    const orderedPref = MOBILE_BALANCE_ORDER
      .map((k) => pref.find(([kk]) => kk === k))
      .filter(Boolean);
    rest.sort(([a], [b]) => a.localeCompare(b));
    return [...orderedPref, ...rest];
  }, [balances]);

  // ──────────────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-blue-50 dark:bg-slate-900">
        <motion.h2
          className="text-2xl font-semibold mb-4 text-red-600 dark:text-red-400"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {error}
        </motion.h2>
        <button
          onClick={() => navigate("/login")}
          className="mt-4 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          Go to Login
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 dark:bg-slate-900 p-6 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-28 w-full" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-72" />
        </div>
        <Skeleton className="h-60" />
        <Skeleton className="h-60" />
      </div>
    );
  }

  if (!user || balances === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-blue-50 dark:bg-slate-900">
        <p className="text-blue-600 dark:text-slate-200 text-lg">Loading dashboard...</p>
      </div>
    );
  }

  // Original desktop order/render
  const balanceEntries = Object.entries(balances);

  return (
    <motion.div
      className="min-h-screen bg-blue-50 dark:bg-slate-900 pb-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
{/* ───────────────── Header (mobile keeps L/R) ───────────────── */}
<header className="bg-white dark:bg-slate-800 shadow-lg border-b-4 border-blue-600 dark:border-blue-600/70">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
    <div className="flex flex-row items-start justify-between gap-3">
      {/* Left: greeting */}
      <div className="min-w-0">
        <h1 className="text-2xl sm:text-3xl font-bold text-blue-800 dark:text-slate-100 leading-tight">
          Welcome back, {user?.firstName || user?.name || "Trader"}!
        </h1>
        <p className="text-blue-600 dark:text-slate-300 text-sm sm:text-base">
          Your classic portfolio overview
        </p>
      </div>

      {/* Right: totals */}
      <div className="text-right shrink-0">
        <p className="text-[11px] sm:text-sm text-gray-600 dark:text-slate-300">Total Portfolio Value</p>
        <p className="text-xl sm:text-2xl font-extrabold text-blue-700 dark:text-blue-400 leading-snug">
          {formatINR(totalValueINR)}
        </p>
        {mdUpdatedAt && (
          <p className="text-[10px] sm:text-xs text-gray-500 dark:text-slate-400 mt-0.5 whitespace-nowrap">
            Market update: {mdUpdatedAt.toLocaleTimeString()}
          </p>
        )}
      </div>
    </div>
  </div>
</header>

      <main className="max-w-7xl mx-auto w-full md:px-6 px-3 md:mt-6 mt-3 md:space-y-8 space-y-3">
{/* ───────────────── Quick Actions (phone-compact like Withdraw) ───────────────── */}
<motion.div
  className="bg-white dark:bg-slate-800 md:rounded-2xl rounded-xl md:shadow-lg shadow md:p-6 p-3 md:flex md:items-center md:justify-between md:gap-4 border-t-4 md:border-t-0 md:border-l-4 border-blue-500 dark:border-blue-500/70"
  initial={{ y: 20, opacity: 0 }}
  animate={{ y: 0, opacity: 1 }}
  transition={{ duration: 0.4 }}
>
  <p className="text-blue-700 dark:text-slate-100 font-medium md:mb-0 mb-2 text-sm md:text-base">Quick Actions</p>
  <div className="flex flex-wrap gap-2 md:gap-3 md:ml-auto">
    <button
      onClick={() => navigate("/deposit")}
      className="bg-blue-600 text-white px-3 md:px-5 py-1.5 md:py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base"
    >
      Deposit
    </button>
    <button
      onClick={() => navigate("/withdraw")}
      className="bg-blue-600 text-white px-3 md:px-5 py-1.5 md:py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base"
    >
      Withdraw
    </button>
    <button
      onClick={() => navigate("/quick-trade")}
      className="bg-blue-600 text-white px-3 md:px-5 py-1.5 md:py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base"
    >
      Quick Trade
    </button>
    <button
      onClick={() => navigate("/p2p")}
      className="bg-blue-600 text-white px-3 md:px-5 py-1.5 md:py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base"
    >
      P2P Market
    </button>
  </div>
</motion.div>

        {/* ───────────────── Balances + Pie ───────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Desktop/Tablet balances (UNCHANGED layout) */}
          <div className="hidden sm:grid lg:col-span-2 grid-cols-1 sm:grid-cols-2 gap-6">
            {balanceEntries.length === 0 && (
              <div className="col-span-2 bg-white dark:bg-slate-800 rounded-2xl p-8 text-blue-700 dark:text-slate-200 text-center shadow-md">
                You have no balances yet. Start by making a deposit or quick trade.
              </div>
            )}

            {balanceEntries.map(([currency, amount], idx) => {
              const amt = Number(amount) || 0;
              const isINR = currency === "INR";
              const inr = isINR ? amt : amt * (priceLookup[currency] || 0);
              return (
                <motion.div
                  key={currency}
                  className="bg-white dark:bg-slate-800 rounded-2xl shadow-md p-6 flex flex-col gap-2 hover:shadow-xl transition-shadow duration-300 border-t-4 border-blue-500 dark:border-blue-500/70"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 + idx * 0.05, duration: 0.4 }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center">
                        <span className="text-blue-700 dark:text-blue-300 font-bold">{currency[0]}</span>
                      </div>
                      <h3 className="text-xl font-semibold text-blue-800 dark:text-slate-100">{currency}</h3>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-slate-300">{formatINR(inr)}</span>
                  </div>
                  <div className="text-3xl font-bold text-blue-700 dark:text-blue-300 break-all">
                    {isINR ? formatINR(amt) : formatNum(amt, 6)}
                    {!isINR && <span className="text-base text-blue-500 dark:text-blue-400 ml-1">{currency}</span>}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Mobile balances: square cards (UNCHANGED layout, dark variants added) */}
          <div className="sm:hidden">
            {sortedBalanceEntries.length === 0 ? (
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 text-blue-700 dark:text-slate-200 text-center shadow-md border-t-4 border-blue-500 dark:border-blue-500/70">
                You have no balances yet. Start by making a deposit or quick trade.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {sortedBalanceEntries.map(([currency, amount], idx) => {
                  const amt = Number(amount) || 0;
                  const isINR = currency === "INR";
                  const inr = isINR ? amt : amt * (priceLookup[currency] || 0);
                  return (
                    <motion.div
                      key={currency}
                      className="bg-white dark:bg-slate-800 rounded-2xl shadow-md p-3 border-t-4 border-blue-500 dark:border-blue-500/70 hover:shadow-lg transition-shadow duration-300 flex flex-col aspect-square min-w-0"
                      initial={{ y: 14, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.08 + idx * 0.05, duration: 0.35 }}
                    >
                      <div className="flex items-start justify-between gap-2 min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="h-9 w-9 bg-blue-100 dark:bg-blue-900/40 rounded-xl flex items-center justify-center shrink-0">
                            <span className="text-blue-700 dark:text-blue-300 font-bold">{currency[0]}</span>
                          </div>
                          <h3
                            className="text-sm font-semibold text-blue-800 dark:text-slate-100 truncate max-w-[7.5rem]"
                            title={currency}
                          >
                            {currency}
                          </h3>
                        </div>
                        <span
                          className="text-[10px] text-gray-500 dark:text-slate-300 whitespace-nowrap ml-2 shrink-0"
                          title={formatINR(inr)}
                        >
                          {formatINR(inr)}
                        </span>
                      </div>

                      <div className="flex-1 flex items-center min-w-0">
                        <div
                          className="text-xl font-bold text-blue-700 dark:text-blue-300 leading-snug tracking-tight tabular-nums min-w-0 break-words"
                          title={isINR ? formatINR(amt) : `${formatNum(amt, 6)} ${currency}`}
                        >
                          {isINR ? (
                            <span className="break-words">{formatINR(amt)}</span>
                          ) : (
                            <>
                              <span className="break-words">{formatNum(amt, 6)}</span>
                              <span className="text-xs text-blue-500 dark:text-blue-400 ml-1 align-baseline">
                                {currency}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Portfolio Pie (layout unchanged, container themed) */}
          <motion.div
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-md p-6 flex flex-col border-t-4 border-blue-500 dark:border-blue-500/70"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.4 }}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xl font-semibold text-blue-800 dark:text-slate-100">Portfolio Distribution</h3>
              <button
                onClick={() => window.location.reload()}
                className="text-sm px-3 py-1 rounded-lg border border-blue-200 dark:border-slate-600 text-blue-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-slate-700/60"
                title="Refresh"
              >
                Refresh
              </button>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={pieDataINR.length ? pieDataINR : [{ name: "No Balances", value: 1 }]}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={85}
                  labelLine={false}
                  label={({ name, percent }) =>
                    pieDataINR.length ? `${name} ${(percent * 100).toFixed(0)}%` : "No Balances"
                  }
                >
                  {(pieDataINR.length ? pieDataINR : [{ name: "No Balances", value: 1 }]).map((_, idx) => (
                    <Cell key={idx} fill={pieDataINR.length ? COLORS[idx % COLORS.length] : EMPTY_COLOR} />
                  ))}
                </Pie>
                {pieDataINR.length > 0 && (
                  <>
                    <Tooltip
                      formatter={(value, name) => [formatINR(value), name]}
                      contentStyle={{
                        background: "var(--tw-tooltip-bg, white)",
                        border: "1px solid rgba(148,163,184,0.35)",
                        color: "inherit",
                      }}
                      wrapperStyle={{ outline: "none" }}
                    />
                    <Legend verticalAlign="bottom" height={36} />
                  </>
                )}
              </PieChart>
            </ResponsiveContainer>
            {pieDataINR.length > 0 && (
              <p className="mt-2 text-xs text-gray-500 dark:text-slate-300">Values shown are INR-weighted for accuracy.</p>
            )}
          </motion.div>
        </div>

        {/* ───────────────── Market Overview (layout unchanged) ───────────────── */}
        <motion.div
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6 border-t-4 border-blue-500 dark:border-blue-500/70"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.4 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-blue-800 dark:text-slate-100">Market Overview</h3>
            {mdUpdatedAt && (
              <span className="text-xs text-gray-500 dark:text-slate-300">Last update {mdUpdatedAt.toLocaleTimeString()}</span>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full table-auto text-blue-800 dark:text-slate-200">
              <thead>
                <tr className="bg-blue-50/70 dark:bg-slate-700/60">
                  <th className="px-4 py-2 text-left text-sm font-medium">Pair</th>
                  <th className="px-4 py-2 text-right text-sm font-medium">Price (₹)</th>
                  <th className="px-4 py-2 text-right text-sm font-medium">24h Change</th>
                </tr>
              </thead>
              <tbody>
                {marketData.length > 0 ? (
                  marketData.map((item) => (
                    <tr
                      key={item.pair}
                      className="border-b last:border-b-0 border-blue-100 dark:border-slate-700 hover:bg-blue-50 dark:hover:bg-slate-700/50 transition"
                    >
                      <td className="px-4 py-3">{item.pair}</td>
                      <td className="px-4 py-3 text-right">
                        {Number(item.price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                            item.change >= 0
                              ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                              : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                          }`}
                        >
                          {item.change >= 0 ? "+" : ""}{Number(item.change || 0).toFixed(2)}%
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-blue-500 dark:text-slate-300">
                      Loading market data…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* ───────────────── Recent Orders ───────────────── */}
        <motion.div
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6 border-t-4 border-blue-500 dark:border-blue-500/70"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.4 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-blue-800 dark:text-slate-100">Recent Orders</h3>
            <button
              onClick={() => navigate("/exchange")}
              className="text-sm px-3 py-1 rounded-lg border border-blue-200 dark:border-slate-600 text-blue-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-slate-700/60"
            >
              Go to Exchange
            </button>
          </div>

          {/* Mobile: card list (layout unchanged) */}
          <div className="sm:hidden space-y-3">
            {orders && orders.length > 0 ? (
              orders.slice(0, 10).map((order) => (
                <div
                  key={order._id}
                  className="rounded-xl border border-blue-100 dark:border-slate-700 bg-blue-50/40 dark:bg-slate-700/40 p-4"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-slate-300">Order ID</span>
                    <span className="text-[11px] font-mono text-gray-700 dark:text-slate-200">{order._id}</span>
                  </div>
                  <div className="mt-1 grid grid-cols-2 gap-y-1 text-sm">
                    <div className="text-gray-500 dark:text-slate-300">Pair</div>
                    <div className="text-blue-900 dark:text-slate-100 font-medium text-right">{order.pair}</div>

                    <div className="text-gray-500 dark:text-slate-300">Type</div>
                    <div className={`text-right font-semibold ${
                      String(order.type || "").toLowerCase() === "buy"
                        ? "text-green-600 dark:text-green-300" : "text-red-600 dark:text-red-300"
                    }`}>
                      {String(order.type || "").charAt(0).toUpperCase() + String(order.type || "").slice(1)}
                    </div>

                    <div className="text-gray-500 dark:text-slate-300">Amount</div>
                    <div className="text-right">{formatNum(order.amount, 6)}</div>

                    <div className="text-gray-500 dark:text-slate-300">Price</div>
                    <div className="text-right">
                      {Number(order.price || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2, maximumFractionDigits: 2
                      })}
                    </div>

                    <div className="text-gray-500 dark:text-slate-300">Status</div>
                    <div className="text-right">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                          order.status === "Filled"
                            ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                            : order.status === "Pending"
                            ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300"
                            : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                        }`}
                      >
                        {order.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-blue-600 dark:text-slate-300 py-6">
                No orders to display. Place your first order in{" "}
                <button
                  className="underline text-blue-700 dark:text-blue-400"
                  onClick={() => navigate("/quick-trade")}
                >
                  Quick Trade
                </button>.
              </div>
            )}
          </div>

          {/* Desktop/Tablet table (layout unchanged) */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full table-auto text-blue-800 dark:text-slate-200">
              <thead>
                <tr className="bg-blue-50/70 dark:bg-slate-700/60">
                  <th className="px-4 py-2 text-left  text-sm font-medium">Order ID</th>
                  <th className="px-4 py-2 text-left  text-sm font-medium">Pair</th>
                  <th className="px-4 py-2 text-right text-sm font-medium">Type</th>
                  <th className="px-4 py-2 text-right text-sm font-medium">Amount</th>
                  <th className="px-4 py-2 text-right text-sm font-medium">Price</th>
                  <th className="px-4 py-2 text-center text-sm font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {orders && orders.length > 0 ? (
                  orders.slice(0, 10).map((order) => (
                    <tr
                      key={order._id}
                      className="border-b last:border-b-0 border-blue-100 dark:border-slate-700 hover:bg-blue-50 dark:hover:bg-slate-700/50 transition"
                    >
                      <td className="px-4 py-3 break-all">{order._id}</td>
                      <td className="px-4 py-3">{order.pair}</td>
                      <td
                        className={`px-4 py-3 text-right font-medium ${
                          String(order.type || "").toLowerCase() === "buy" ? "text-green-600 dark:text-green-300" : "text-red-600 dark:text-red-300"
                        }`}
                      >
                        {String(order.type || "").charAt(0).toUpperCase() + String(order.type || "").slice(1)}
                      </td>
                      <td className="px-4 py-3 text-right">{formatNum(order.amount, 6)}</td>
                      <td className="px-4 py-3 text-right">
                        {Number(order.price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            order.status === "Filled"
                              ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                              : order.status === "Pending"
                              ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300"
                              : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                          }`}
                        >
                          {order.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-blue-600 dark:text-slate-300">
                      No orders to display. Place your first order in{" "}
                      <button className="underline text-blue-700 dark:text-blue-400" onClick={() => navigate('/quick-trade')}>
                        Quick Trade
                      </button>.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </main>
    </motion.div>
  );
};

export default Dashboard;
